import React from "react";
import { CheckOutlined } from "@ant-design/icons";

const PRIMARY = "#1890ff";
const RAIL_DONE = PRIMARY;
const RAIL_TODO = "#d9d9d9";

/**
 * Horizontal stepper: completed (primary fill + white tick), current (primary ring + index),
 * upcoming (gray ring). Dotted rails follow completed vs upcoming segments.
 */
const AppSteps = ({ steps = [], currentStep = 0, onChange, className = "" }) => {
  const n = steps.length;
  if (!n) return null;

  return (
    <div
      className={`app-steps w-full select-none ${className}`.trim()}
      role="list"
      aria-label="Steps"
    >
      <div className="flex w-full items-start">
        {steps.map((step, i) => {
          const title = step.title ?? step.label ?? "";
          const isDone = i < currentStep;
          const isCurrent = i === currentStep;

          const leftRailColor =
            i === 0 ? "transparent" : i - 1 < currentStep ? RAIL_DONE : RAIL_TODO;
          const rightRailColor =
            i === n - 1 ? "transparent" : i < currentStep ? RAIL_DONE : RAIL_TODO;

          const labelStrong = isDone || isCurrent;

          return (
            <div
              key={step.key ?? i}
              className="app-steps__segment flex min-w-0 flex-1 flex-col items-center"
              role="listitem"
            >
              <div className="flex h-10 w-full max-w-full items-center">
                <div
                  className="min-h-0 flex-1 border-t-2 border-dotted"
                  style={{ borderColor: leftRailColor }}
                  aria-hidden
                />
                <button
                  type="button"
                  tabIndex={0}
                  className={`relative z-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    isDone
                      ? "border-primary bg-primary text-white shadow-sm"
                      : isCurrent
                        ? "border-primary bg-white text-primary"
                        : "border-[#d9d9d9] bg-white font-medium text-[#8c8c8c]"
                  }`}
                  onClick={() => onChange?.(i)}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-label={`${title}, step ${i + 1} of ${n}${isDone ? ", completed" : ""}${isCurrent ? ", current" : ""}`}
                >
                  {isDone ? (
                    <CheckOutlined
                      className="text-[17px] font-bold leading-none text-white anticon"
                      aria-hidden
                    />
                  ) : (
                    String(i + 1).padStart(2, "0")
                  )}
                </button>
                <div
                  className="min-h-0 flex-1 border-t-2 border-dotted"
                  style={{ borderColor: rightRailColor }}
                  aria-hidden
                />
              </div>
              <span
                className={`mt-3 max-w-[10rem] px-1 text-center text-sm leading-tight ${
                  labelStrong
                    ? "font-semibold text-primary"
                    : "font-normal text-[#8c8c8c]"
                }`}
              >
                {title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppSteps;
